import { useSelector } from 'react-redux';
import { selectOrgPermissions, selectAccountPermissions } from '../reducers/userInfoReducer';
import { useCallback } from 'react';
import { PermissionQuery } from './permissions.types';

export const usePermissions = () => {
  const orgPermissions = useSelector(selectOrgPermissions);
  const accountPermissions = useSelector(selectAccountPermissions);

  /**
   * Evaluates an array of permission queries.
   * Each query can be either:
   * - CrmOrgPermissionQuery: { permission: CrmOrgPermission, crmOrgId: string } - checks org-level permissions
   * - GlobalPermissionQuery: { permission: GlobalPermission } - checks global/account-level permissions
   * @param permissionQueries - Array of permission queries to evaluate
   * @returns An array of booleans, where each boolean indicates if the corresponding permission is granted.
   */

  const evaluateSinglePermission = useCallback((permissionQuery: PermissionQuery) => {
    if ('crmOrgId' in permissionQuery) {
      if (Array.isArray(permissionQuery.permission)) {
        return permissionQuery.permission.map(p => orgPermissions?.[permissionQuery.crmOrgId]?.includes(p) ?? false)
      } else {
        return orgPermissions?.[permissionQuery.crmOrgId]?.includes(permissionQuery.permission) ?? false;
      }
    }
    if (Array.isArray(permissionQuery.permission)) {
      return permissionQuery.permission.map(p => accountPermissions?.includes(p) ?? false)
    } else {
      return accountPermissions?.includes(permissionQuery.permission) ?? false;
    }
  }, [orgPermissions, accountPermissions]);

  const evaluatePermissions = useCallback((permissionQueries: PermissionQuery[]) => {
    return permissionQueries.map(evaluateSinglePermission).flat();
  }, [evaluateSinglePermission]);

  const hasEveryPermission = useCallback((permissionQueries: PermissionQuery[]) => {
    return evaluatePermissions(permissionQueries).every(Boolean);
  }, [evaluatePermissions]);


  const hasAnyPermissions = useCallback((permissions: PermissionQuery[]): boolean => {
    return evaluatePermissions(permissions).some(Boolean);
  }, [evaluatePermissions]);

  return {
    evaluatePermissions,
    hasEveryPermission,
    hasAnyPermissions,
  };
};
